import { zodResolver } from "@hookform/resolvers/zod";
import type { JSX } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { changePasswordSchema, type ChangePasswordFormValues } from "../lib/change-password-schema";

interface ChangePasswordFormProps {
  onSubmit: (values: ChangePasswordFormValues) => void;
  isLoading: boolean;
  serverError?: string;
}

export function ChangePasswordForm({
  onSubmit,
  isLoading,
  serverError,
}: ChangePasswordFormProps): JSX.Element {
  const { t } = useTranslation("auth");
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ChangePasswordFormValues>({ resolver: zodResolver(changePasswordSchema) });

  return (
    <form
      onSubmit={handleSubmit((data) => onSubmit(data))}
      noValidate
      data-testid="change-password-form"
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="cp-old" className="block text-sm font-medium text-[var(--text-primary)]">
            {t("field.old_password")}
          </label>
          <input
            id="cp-old"
            type="password"
            autoComplete="current-password"
            disabled={isLoading}
            data-testid="change-password-old"
            className="mt-1 w-full rounded border border-[var(--border-default)] px-3 py-2 text-sm outline-none focus:border-[var(--border-focus)] disabled:opacity-50"
            {...register("old_password")}
          />
          {errors.old_password && (
            <p
              className="mt-1 text-xs text-[var(--intent-danger-fg)]"
              data-testid="change-password-old-error"
            >
              {errors.old_password.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="cp-new" className="block text-sm font-medium text-[var(--text-primary)]">
            {t("field.new_password")}
          </label>
          <input
            id="cp-new"
            type="password"
            autoComplete="new-password"
            disabled={isLoading}
            data-testid="change-password-new"
            className="mt-1 w-full rounded border border-[var(--border-default)] px-3 py-2 text-sm outline-none focus:border-[var(--border-focus)] disabled:opacity-50"
            {...register("new_password")}
          />
          {errors.new_password && (
            <p
              className="mt-1 text-xs text-[var(--intent-danger-fg)]"
              data-testid="change-password-new-error"
            >
              {errors.new_password.message}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="cp-confirm"
            className="block text-sm font-medium text-[var(--text-primary)]"
          >
            {t("field.confirm_password")}
          </label>
          <input
            id="cp-confirm"
            type="password"
            autoComplete="new-password"
            disabled={isLoading}
            data-testid="change-password-confirm"
            className="mt-1 w-full rounded border border-[var(--border-default)] px-3 py-2 text-sm outline-none focus:border-[var(--border-focus)] disabled:opacity-50"
            {...register("confirm_password")}
          />
          {errors.confirm_password && (
            <p
              className="mt-1 text-xs text-[var(--intent-danger-fg)]"
              data-testid="change-password-confirm-error"
            >
              {errors.confirm_password.message}
            </p>
          )}
        </div>

        {serverError && (
          <p
            className="text-sm text-[var(--intent-danger-fg)]"
            data-testid="change-password-server-error"
          >
            {serverError}
          </p>
        )}

        <button
          type="submit"
          disabled={isLoading}
          data-testid="change-password-submit"
          className="w-full rounded bg-[var(--brand-500)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--brand-600)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? "…" : t("action.change_password")}
        </button>
      </div>
    </form>
  );
}
